import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Collapse, TableCell, TableRow } from '@mui/material';

import OnCreateIcon from '../../../icons/OnCreateIcon';
import OnEditIcon from '../../../icons/OnEditIcon';
import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadParticipantByPtCode } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import CheckBox from '../../formElements/CheckBox';

import ValidationRowDetails from './ValidationRowDetails';

import table from '../../../commonStyles/tables.module.css';
import validations from '../claimStyles/validations.module.css';

const styles = {
    paddingBottom: 0,
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    borderTop: 'none',
    borderBottom: 'none'
};

const ValidationTableRow = ({
    row,
    isDisabled = true,
    isAccessRestricted = false,
    toggleDropdown,
    openId,
    onStatusRowChange,
    onEditAction,
    onCreateAction,
    onRowChange
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [open, setOpen] = useState(null);
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USE EFFECTS
    useEffect(() => {
        if (openId !== row.id) setOpen(null);
    }, [openId]);

    useEffect(() => {
        if (isPTRequestSent && currentParticipant.ptCode === row.ptCode) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isPTRequestSent, currentParticipant]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === row.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onToggleDropdown = () => {
        toggleDropdown(row.id);
        setOpen(open === row.id ? null : row.id);
    };

    const onClickPTCode = () => {
        if (row?.ptCode) {
            dispatch(loadParticipantByPtCode(row.ptCode));
            setIsPTRequestSent(true);
        }
    };

    const onChangeStatus = () => row?.statusId && onStatusRowChange(row);

    const onCheck = (e) => {
        const { checked } = e.target;
        const updatedRow = {
            ...row,
            checked: checked
        };
        onRowChange(updatedRow);
    };

    // RENDER
    return (
        <>
            <TableRow
                key={row.id}
                hover
                className={`${table.tableRowStripes} ${table.oldLace} ${table.canOpen}`}>
                <TableCell disabled={isDisabled}>
                    <div className={validations.check}>
                        <CheckBox
                            id={row.id}
                            checked={row.checked}
                            onChange={onCheck}
                            disabled={isDisabled}
                        />
                    </div>
                </TableCell>
                <TableCell
                    className={!isAccessRestricted ? table.rowLink : ''}
                    onClick={!isAccessRestricted ? onClickPTCode : null}>
                    {row?.ptCode}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>{row?.poNumber}</TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {row?.eventTypeDisplayName ? row.eventTypeDisplayName : row.eventType}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.eventDate)}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.lastStatusUpdated)}
                </TableCell>
                <TableCell
                    className={!isDisabled ? table.rowLink : ''}
                    data-testid="row_status_link"
                    onClick={!isDisabled ? onChangeStatus : null}>
                    {row.statusName}
                </TableCell>
                <TableCell align="center">
                    {row.actionStatus === 'zz' ? ' - ' : row.actionStatus}
                </TableCell>
                <TableCell align="center">
                    {row.actionStatus === 'zz' ? (
                        <OnCreateIcon
                            onCreate={() => onCreateAction(row)}
                            titleString={'Action'}
                            active={!isDisabled}
                        />
                    ) : (
                        <OnEditIcon onEdit={() => onEditAction(row)} active={!isDisabled} />
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={styles} colSpan={9}>
                    <Collapse in={open === row.id} timeout="auto" unmountOnExit>
                        <ValidationRowDetails row={row} onClose={() => setOpen(null)} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

ValidationTableRow.propTypes = {
    row: PropTypes.object,
    toggleDropdown: PropTypes.func,
    openId: PropTypes.string,
    onStatusRowChange: PropTypes.func,
    onCreateAction: PropTypes.func,
    onEditAction: PropTypes.func,
    onRowChange: PropTypes.func,
    isDisabled: PropTypes.bool,
    isAccessRestricted: PropTypes.bool
};

export default ValidationTableRow;
